// Master Event service - manages events, memberships, and activities
// Dual-write: localStorage (sync, primary return) + DynamoDB (async, fire-and-forget)
import { getCurrentUser } from 'aws-amplify/auth';
import { authService } from './auth';
import * as db from './db';
import { competitionService } from './competition';

const ME_KEY = 'betbuds_master_events';
const MEMBERS_KEY = 'betbuds_memberships';
const ACTIVITIES_KEY = 'betbuds_activities';

function getStored(key, fallback = []) {
  const stored = localStorage.getItem(key);
  if (stored) return JSON.parse(stored);
  localStorage.setItem(key, JSON.stringify(fallback));
  return fallback;
}

function save(key, data) {
  localStorage.setItem(key, JSON.stringify(data));
}

// Fire-and-forget helper — always logs errors, never throws
// AWSJSON fields come back as JSON-encoded strings; older rows may hold a plain
// string (pre-schema-change), so fall back to wrapping it as { name } on parse failure.
function parseLocation(value) {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return { name: value, link: null }; }
}

function parseJsonField(value, fallback = value) {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return fallback; }
}

function normalizeActivity(record) {
  if (!record) return record;
  return {
    ...record,
    id: record.activityId || record.id,
    pointsConfig: parseJsonField(record.pointsConfig),
    location: parseLocation(record.location),
    activityEditors: record.activityEditors || [],
  };
}

function dbWrite(label, promise) {
  promise
    .then((result) => console.log(`[masterEvent] DynamoDB ${label} succeeded:`, result))
    .catch((err) => console.error(`[masterEvent] DynamoDB ${label} FAILED:`, err));
}

function getLocalUsers() {
  try { return authService.getAllUsers() || []; } catch { return []; }
}

function normalizeEmail(email) {
  return email?.trim().toLowerCase();
}

function getLocalUserByAnyId(userId) {
  const inviteEmail = userId?.startsWith?.('invite:') ? userId.slice(7) : null;
  return getLocalUsers().find((u) =>
    u.id === userId ||
    u.userId === userId ||
    u.cognitoId === userId ||
    (inviteEmail && normalizeEmail(u.email) === inviteEmail)
  );
}

function getUserIdCandidates(userId) {
  const localUser = getLocalUserByAnyId(userId);
  const candidates = new Set([userId]);
  if (localUser) {
    [localUser.id, localUser.userId, localUser.cognitoId].forEach((id) => {
      if (id) candidates.add(id);
    });
    const email = normalizeEmail(localUser.email);
    if (email) candidates.add(`invite:${email}`);
  }
  return [...candidates].filter(Boolean);
}

function findLocalMembership(members, masterEventId, userIds) {
  const candidates = new Set(userIds);
  return members.find((m) => m.masterEventId === masterEventId && candidates.has(m.userId));
}

async function findDbMembership(masterEventId, userId) {
  const candidates = new Set(getUserIdCandidates(userId));
  const memberships = await db.listMembershipsByEvent(masterEventId);
  return memberships.find((m) => candidates.has(m.userId)) || null;
}

async function getMembershipUserId(userId) {
  if (!userId || userId.startsWith?.('invite:')) return userId;

  const localUser = getLocalUserByAnyId(userId);
  if (localUser?.cognitoId) return localUser.cognitoId;

  const lookupIds = [...new Set([userId, localUser?.id, localUser?.userId].filter(Boolean))];
  for (const id of lookupIds) {
    try {
      const dbUser = await db.getUser(id);
      if (dbUser?.cognitoId) return dbUser.cognitoId;
      if (dbUser?.userId) return dbUser.userId;
    } catch (_) { /* keep trying */ }
  }

  const email = normalizeEmail(localUser?.email);
  if (email) {
    try {
      const dbUser = await db.getUserByEmail(email);
      if (dbUser?.cognitoId) return dbUser.cognitoId;
      if (dbUser?.userId) return dbUser.userId;
    } catch (_) { /* fall back to supplied id */ }
  }

  return userId;
}

async function getCurrentCognitoUserId() {
  try {
    return (await getCurrentUser()).userId;
  } catch (_) {
    return null;
  }
}

function isActivityEditorRole(role) {
  return ['owner', 'event-admin', 'co-organizer', 'activity-admin'].includes(role);
}

function addEditorId(editorIds, userId) {
  if (!userId || userId.startsWith?.('invite:')) return;
  editorIds.add(userId);
}

function isActivityAuthError(err) {
  return /not authorized/i.test(err?.message || '') && /updateActivit/i.test(err?.message || '');
}

async function getActivityEditorIds(masterEventId) {
  const editorIds = new Set();
  addEditorId(editorIds, await getCurrentCognitoUserId());

  try {
    const event = await db.getMasterEvent(masterEventId);
    addEditorId(editorIds, event?.createdBy);
  } catch (_) { /* keep building from memberships */ }

  let memberships = [];
  try {
    memberships = await db.listMembershipsByEvent(masterEventId);
  } catch (_) {
    memberships = getStored(MEMBERS_KEY).filter((m) => m.masterEventId === masterEventId);
  }

  for (const membership of memberships) {
    if (!isActivityEditorRole(membership.role)) continue;
    addEditorId(editorIds, membership.userId);
    try {
      addEditorId(editorIds, await getMembershipUserId(membership.userId));
    } catch (_) { /* local fallback id may already be included */ }
    const localUser = getLocalUserByAnyId(membership.userId);
    addEditorId(editorIds, localUser?.cognitoId);
  }

  return [...editorIds];
}

export const masterEventService = {
  // ===== Master Events =====
  async getAll(currentUser) {
    try {
      const cognitoUser = await getCurrentUser();
      const cognitoUserId = cognitoUser.userId;
      const isSuperAdmin = currentUser?.role === 'admin';

      let events;
      if (isSuperAdmin) {
        console.log('[masterEvent] getAll: superadmin — fetching ALL MasterEvents from DynamoDB');
        events = await db.listMasterEvents();
      } else {
        console.log('[masterEvent] getAll: regular user — querying memberships for', cognitoUserId);
        const memberships = await db.listMembershipsByUser(cognitoUserId);
        const eventIds = [...new Set(memberships.map((m) => m.masterEventId))];
        events = (await Promise.all(eventIds.map((id) => db.getMasterEvent(id)))).filter(Boolean);
      }

      if (events && events.length > 0) {
        // Normalize DynamoDB shape to match localStorage shape (eventId -> id)
        const normalized = events.map((e) => ({
          ...e,
          id: e.eventId || e.id,
        }));
        console.log(`[masterEvent] getAll: DynamoDB returned ${normalized.length} events`);
        return normalized;
      }
    } catch (err) {
      console.warn('[masterEvent] getAll: DynamoDB fetch failed, falling back to localStorage:', err);
    }

    // Fallback to localStorage
    console.log('[masterEvent] getAll: using localStorage fallback');
    return getStored(ME_KEY);
  },

  async getById(id) {
    try {
      const event = await db.getMasterEvent(id);
      if (event) {
        return { ...event, id: event.eventId || event.id };
      }
    } catch (err) {
      console.warn('[masterEvent] getById: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    return getStored(ME_KEY).find((e) => e.id === id) || null;
  },

  async getForUser(userId) {
    try {
      const cognitoUser = await getCurrentUser();
      const cognitoUserId = cognitoUser.userId;
      console.log('[masterEvent] getForUser: querying DynamoDB memberships for Cognito userId:', cognitoUserId);
      const memberships = await db.listMembershipsByUser(cognitoUserId);
      if (memberships && memberships.length > 0) {
        const eventIds = [...new Set(memberships.map((m) => m.masterEventId))];
        const events = (await Promise.all(eventIds.map((id) => db.getMasterEvent(id)))).filter(Boolean);
        if (events.length > 0) {
          const normalized = events.map((e) => ({ ...e, id: e.eventId || e.id }));
          console.log(`[masterEvent] getForUser: DynamoDB returned ${normalized.length} events`);
          return normalized;
        }
      }
    } catch (err) {
      console.warn('[masterEvent] getForUser: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    // Fallback to localStorage
    const memberships = getStored(MEMBERS_KEY);
    const eventIds = new Set(memberships.filter((m) => m.userId === userId).map((m) => m.masterEventId));
    return getStored(ME_KEY).filter((e) => eventIds.has(e.id));
  },

  create({ name, description, type, createdBy }) {
    const events = getStored(ME_KEY);
    const newEvent = {
      id: `me_${Date.now()}`,
      name,
      description: description || '',
      type: type || 'gathering',
      status: 'active',
      createdAt: new Date().toISOString(),
      createdBy,
      obfuscateActivities: false,
    };
    events.push(newEvent);
    save(ME_KEY, events);

    // Add creator as owner
    const members = getStored(MEMBERS_KEY);
    const membership = {
      id: `mem_${Date.now()}`,
      masterEventId: newEvent.id,
      userId: createdBy,
      role: 'owner',
      status: 'active',
      joinedAt: new Date().toISOString(),
    };
    members.push(membership);
    save(MEMBERS_KEY, members);

    // DynamoDB dual-write — use Cognito userId for createdBy and membership
    getCurrentUser()
      .then((cognitoUser) => {
        const cognitoUserId = cognitoUser.userId;

        const masterEventInput = {
          eventId: newEvent.id,
          name: newEvent.name,
          description: newEvent.description,
          type: newEvent.type,
          status: newEvent.status,
          createdAt: newEvent.createdAt,
          createdBy: cognitoUserId,
          obfuscateActivities: newEvent.obfuscateActivities,
        };
        console.log('[masterEvent] Creating MasterEvent in DynamoDB, input:', masterEventInput);
        dbWrite('createMasterEvent', db.createMasterEvent(masterEventInput));

        const membershipInput = {
          masterEventId: newEvent.id,
          userId: cognitoUserId,
          role: 'owner',
          status: 'active',
          joinedAt: membership.joinedAt,
        };
        console.log('[masterEvent] Creating Membership in DynamoDB with Cognito userId:', membershipInput);
        dbWrite('createMembership(owner)', db.createMembership(membershipInput));
      })
      .catch((err) => console.error('[masterEvent] DynamoDB dual-write failed (no Cognito user):', err));

    return newEvent;
  },

  async update(id, updates) {
    const savedEvent = await db.updateMasterEvent(id, updates);
    const events = getStored(ME_KEY);
    const idx = events.findIndex((e) => e.id === id);
    const normalizedSaved = savedEvent ? { ...savedEvent, id: savedEvent.eventId || savedEvent.id } : {};
    const nextEvent = idx === -1
      ? { id, ...updates, ...normalizedSaved }
      : { ...events[idx], ...updates, ...normalizedSaved };
    if (idx === -1) events.push(nextEvent);
    else events[idx] = nextEvent;
    save(ME_KEY, events);

    return nextEvent;
  },

  async conclude(id) {
    return this.update(id, { status: 'concluded', concludedAt: new Date().toISOString() });
  },

  delete(id) {
    save(ME_KEY, getStored(ME_KEY).filter((e) => e.id !== id));

    // Clean up memberships
    const removedMembers = getStored(MEMBERS_KEY).filter((m) => m.masterEventId === id);
    save(MEMBERS_KEY, getStored(MEMBERS_KEY).filter((m) => m.masterEventId !== id));

    // Clean up activities
    const removedActivities = getStored(ACTIVITIES_KEY).filter((a) => a.masterEventId === id);
    save(ACTIVITIES_KEY, getStored(ACTIVITIES_KEY).filter((a) => a.masterEventId !== id));

    // DynamoDB dual-write
    dbWrite('deleteMasterEvent', db.deleteMasterEvent(id));
    removedMembers.forEach((m) => dbWrite('deleteMembership', db.deleteMembership(m.masterEventId, m.userId)));
    removedActivities.forEach((a) => dbWrite('deleteActivity', db.deleteActivity(a.id)));
  },

  // ===== Memberships =====
  async getMembers(masterEventId) {
    try {
      const memberships = await db.listMembershipsByEvent(masterEventId);
      if (memberships && memberships.length > 0) {
        const localUsers = authService.getAllUsers();

        // Resolve user info for each membership
        const resolved = await Promise.all(memberships.map(async (m) => {
          // Pending invite — look up placeholder by email
          if (m.userId?.startsWith('invite:')) {
            const email = m.userId.slice(7);
            let placeholder = null;
            try { placeholder = await db.getUserByEmail(email); } catch (_) { /* ignore */ }
            return {
              ...m,
              user: placeholder
                ? { ...placeholder, id: placeholder.userId, displayName: placeholder.displayName, email: placeholder.email, avatar: '📩' }
                : { id: m.userId, displayName: email.split('@')[0], email, avatar: '📩' },
            };
          }

          // Real user — try localStorage first (fast), then DynamoDB
          let user = localUsers.find((u) => u.cognitoId === m.userId || u.id === m.userId || u.userId === m.userId);
          if (!user) {
            try {
              const dbUser = await db.getUserByCognitoId(m.userId);
              if (dbUser) {
                user = { ...dbUser, id: dbUser.userId };
              }
            } catch (_) { /* ignore */ }
          }
          return { ...m, user };
        }));

        return resolved;
      }
    } catch (err) {
      console.warn('[masterEvent] getMembers: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    const memberships = getStored(MEMBERS_KEY).filter((m) => m.masterEventId === masterEventId);
    const localUsers = authService.getAllUsers();
    return memberships.map((m) => ({
      ...m,
      user: localUsers.find((u) => u.id === m.userId || u.userId === m.userId || u.cognitoId === m.userId),
    }));
  },

  async getMembership(masterEventId, userId) {
    try {
      const memberships = await db.listMembershipsByEvent(masterEventId);
      if (memberships && memberships.length > 0) {
        const localUser = getLocalUserByAnyId(userId);
        const candidates = new Set(getUserIdCandidates(userId));

        // Also get the current Cognito user as a fallback identifier
        let currentCognitoId = null;
        try { currentCognitoId = (await getCurrentUser()).userId; } catch (_) { /* not logged in */ }

        // Prefer the user's own ids. Fall back to the signed-in Cognito identity only for a
        // local user with no cognitoId, so impersonation never picks up the owner's role.
        return memberships.find((m) => candidates.has(m.userId))
          || (currentCognitoId && localUser && !localUser.cognitoId && userId === localUser.id
            ? memberships.find((m) => m.userId === currentCognitoId) : null)
          || null;
      }
    } catch (err) {
      console.warn('[masterEvent] getMembership: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    return getStored(MEMBERS_KEY).find((m) => m.masterEventId === masterEventId && m.userId === userId) || null;
  },

  async addMember(masterEventId, userId, role = 'participant') {
    const members = getStored(MEMBERS_KEY);
    const candidates = getUserIdCandidates(userId);
    const existingLocal = findLocalMembership(members, masterEventId, candidates);
    const existingDb = await findDbMembership(masterEventId, userId);
    if (existingLocal || existingDb) {
      throw new Error('User is already a member');
    }
    const localUser = getLocalUserByAnyId(userId);
    const localUserId = localUser?.id || userId;
    const membershipUserId = await getMembershipUserId(userId);
    const joinedAt = new Date().toISOString();

    await db.createMembership({
      masterEventId,
      userId: membershipUserId,
      role,
      status: 'active',
      joinedAt,
    });

    const membership = {
      id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      masterEventId,
      userId: localUserId,
      role,
      status: 'active',
      joinedAt,
    };
    members.push(membership);
    save(MEMBERS_KEY, members);

    return membership;
  },

  async removeMember(masterEventId, userId) {
    const members = getStored(MEMBERS_KEY);
    const dbMembership = await findDbMembership(masterEventId, userId);
    const candidates = new Set([...getUserIdCandidates(userId), dbMembership?.userId].filter(Boolean));
    const localMatches = members.filter((m) => m.masterEventId === masterEventId && candidates.has(m.userId));
    if (!dbMembership && localMatches.length === 0) throw new Error('Membership not found');

    const role = dbMembership?.role || localMatches[0]?.role;
    if (role === 'owner') throw new Error('Cannot remove the owner');

    if (dbMembership) {
      await db.deleteMembership(masterEventId, dbMembership.userId);
    }

    const remaining = members.filter((m) => !(m.masterEventId === masterEventId && candidates.has(m.userId)));
    save(MEMBERS_KEY, remaining);
    return true;
  },

  async updateMemberRole(masterEventId, userId, role) {
    const members = getStored(MEMBERS_KEY);
    const dbMembership = await findDbMembership(masterEventId, userId);
    const candidates = new Set([...getUserIdCandidates(userId), dbMembership?.userId].filter(Boolean));
    const localMatches = members.filter((m) => m.masterEventId === masterEventId && candidates.has(m.userId));
    if (!dbMembership && localMatches.length === 0) throw new Error('Membership not found');

    if (dbMembership) {
      await db.updateMembership(masterEventId, dbMembership.userId, { role });
    }

    localMatches.forEach((mem) => { mem.role = role; });
    save(MEMBERS_KEY, members);

    return { ...(dbMembership || localMatches[0]), role };
  },

  async updateMemberStatus(masterEventId, userId, status) {
    const members = getStored(MEMBERS_KEY);
    const dbMembership = await findDbMembership(masterEventId, userId);
    const candidates = new Set([...getUserIdCandidates(userId), dbMembership?.userId].filter(Boolean));
    const localMatches = members.filter((m) => m.masterEventId === masterEventId && candidates.has(m.userId));
    if (!dbMembership && localMatches.length === 0) throw new Error('Membership not found');

    if (dbMembership) {
      await db.updateMembership(masterEventId, dbMembership.userId, { status });
    }

    localMatches.forEach((mem) => { mem.status = status; });
    save(MEMBERS_KEY, members);

    return { ...(dbMembership || localMatches[0]), status };
  },

  async inviteByEmail(masterEventId, email, displayName, role = 'participant') {
    const normalizedEmail = email.trim().toLowerCase();

    // Check if this user already exists in DynamoDB
    let existingUser = null;
    try {
      existingUser = await db.getUserByEmail(normalizedEmail);
    } catch (_) { /* ignore */ }

    if (existingUser && !existingUser.userId?.startsWith('invite:')) {
      // Existing real user — add them directly to the event
      const cognitoId = existingUser.cognitoId;
      const userId = cognitoId || existingUser.userId;
      console.log('[masterEvent] Existing user found, adding directly to event:', userId);

      const existingMembership = await findDbMembership(masterEventId, userId);
      if (!existingMembership) {
        await db.createMembership({
          masterEventId,
          userId,
          role,
          status: 'active',
          joinedAt: new Date().toISOString(),
        });
      }

      // localStorage membership (backward compat)
      const localUser = authService.getAllUsers().find(
        (u) => u.email?.toLowerCase() === normalizedEmail
      );
      if (localUser) {
        const members = getStored(MEMBERS_KEY);
        const candidates = getUserIdCandidates(localUser.id);
        if (!findLocalMembership(members, masterEventId, candidates)) {
          members.push({
            id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            masterEventId,
            userId: localUser.id,
            role,
            status: 'active',
            joinedAt: new Date().toISOString(),
          });
          save(MEMBERS_KEY, members);
        }
      }

      return { added: true, existing: true, user: existingUser };
    }

    // New user — create placeholder and pending membership
    const pendingUserId = `invite:${normalizedEmail}`;
    // localStorage placeholder
    let localUser;
    try {
      localUser = authService.inviteByEmail(normalizedEmail, displayName, 0);
    } catch (_) {
      // Already exists in localStorage — just find them
      localUser = authService.getAllUsers().find(
        (u) => u.email?.toLowerCase() === normalizedEmail
      );
    }
    if (localUser) {
      const members = getStored(MEMBERS_KEY);
      const candidates = getUserIdCandidates(localUser.id);
      if (!findLocalMembership(members, masterEventId, candidates)) {
        members.push({
          id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          masterEventId,
          userId: localUser.id,
          role,
          status: 'active',
          joinedAt: new Date().toISOString(),
        });
        save(MEMBERS_KEY, members);
      }
    }

    // DynamoDB placeholder user + pending membership
    try {
      if (!existingUser) {
        const username = normalizedEmail.split('@')[0].replace(/[^a-z0-9]/g, '');
        await db.createUser({
          userId: pendingUserId,
          email: normalizedEmail,
          username,
          displayName: displayName || username,
          balance: 0,
          totalWinnings: 0,
          avatar: null,
          avatarType: null,
          role: 'player',
          enabled: true,
          cognitoId: null,
          nickname: null,
        });
        console.log('[masterEvent] Created placeholder DynamoDB user for invite:', pendingUserId);
      }

      await db.createMembership({
        masterEventId,
        userId: pendingUserId,
        role,
        status: 'pending',
        joinedAt: new Date().toISOString(),
      });
      console.log('[masterEvent] Created pending DynamoDB membership for:', pendingUserId);
    } catch (err) {
      console.error('[masterEvent] inviteByEmail DynamoDB write failed:', err);
      throw err;
    }

    return { added: true, existing: false, user: localUser };
  },

  // ===== Activities =====
  async getActivities(masterEventId) {
    try {
      const activities = await db.listActivitiesByEvent(masterEventId);
      if (activities && activities.length > 0) {
        return activities
          .map((a) => normalizeActivity(a))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    } catch (err) {
      console.warn('[masterEvent] getActivities: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    return getStored(ACTIVITIES_KEY)
      .filter((a) => a.masterEventId === masterEventId)
      .sort((a, b) => a.order - b.order);
  },

  async getActivity(activityId) {
    try {
      const activity = await db.getActivity(activityId);
      if (activity) {
        return normalizeActivity(activity);
      }
    } catch (err) {
      console.warn('[masterEvent] getActivity: DynamoDB fetch failed, falling back to localStorage:', err);
    }
    return getStored(ACTIVITIES_KEY).find((a) => a.id === activityId) || null;
  },

  async createActivity({ masterEventId, type, name, description, isTeamBased, isCompetition, pointsConfig, bettingEventId, date, image, imageType, location, transportMode, accommodationType, hint, obfuscated = false, status }) {
    const activities = getStored(ACTIVITIES_KEY);
    const order = activities.filter((a) => a.masterEventId === masterEventId).length;
    const activityEditors = await getActivityEditorIds(masterEventId);
    const activity = {
      id: `act_${Date.now()}`,
      masterEventId,
      type: type || 'custom',
      name: name || 'New Activity',
      description: description || '',
      status: status || 'draft',
      isTeamBased: isTeamBased || false,
      isCompetition: !!(isCompetition || isTeamBased),
      date: date || null,
      image: image || null,
      imageType: imageType || null,
      location: location || null,
      transportMode: transportMode || null,
      accommodationType: accommodationType || null,
      hint: hint || null,
      obfuscated,
      revealed: !obfuscated,
      pointsConfig: pointsConfig || [
        { place: 1, points: 10 },
        { place: 2, points: 7 },
        { place: 3, points: 5 },
        { place: 4, points: 3 },
        { place: 5, points: 1 },
      ],
      bettingEventId: bettingEventId || null,
      order,
      activityEditors,
      createdAt: new Date().toISOString(),
    };
    await db.createActivity({
      activityId: activity.id,
      masterEventId: activity.masterEventId,
      type: activity.type,
      name: activity.name,
      description: activity.description,
      status: activity.status,
      isTeamBased: activity.isTeamBased,
      isCompetition: activity.isCompetition,
      date: activity.date,
      image: activity.image,
      imageType: activity.imageType,
      location: activity.location,
      transportMode: activity.transportMode,
      accommodationType: activity.accommodationType,
      hint: activity.hint,
      obfuscated: activity.obfuscated,
      revealed: activity.revealed,
      pointsConfig: activity.pointsConfig,
      bettingEventId: activity.bettingEventId,
      order: activity.order,
      activityEditors: activity.activityEditors,
    });

    activities.push(activity);
    save(ACTIVITIES_KEY, activities);

    return activity;
  },

  async updateActivity(activityId, updates) {
    const activities = getStored(ACTIVITIES_KEY);
    const idx = activities.findIndex((a) => a.id === activityId || a.activityId === activityId);
    let existingActivity = idx !== -1 ? activities[idx] : null;
    if (!existingActivity) {
      try { existingActivity = normalizeActivity(await db.getActivity(activityId)); } catch (_) { /* update will surface auth/not-found */ }
    }
    const dbUpdates = { ...updates };
    if (typeof updates.obfuscated === 'boolean') dbUpdates.revealed = !updates.obfuscated;
    if (existingActivity?.type === 'betting' && (('isTeamBased' in updates && updates.isTeamBased !== existingActivity.isTeamBased) || ('type' in updates && updates.type !== existingActivity.type))) {
      const state = await competitionService.load(existingActivity.masterEventId, activityId);
      if (state.betting.bets.length || state.betting.drafts.some(d => d.bets.length)) throw new Error('Betting mode cannot change after drafting or submitting bets. Create a new activity for a different mode.');
    }
    if (existingActivity?.masterEventId) {
      dbUpdates.activityEditors = await getActivityEditorIds(existingActivity.masterEventId);
    }

    // Save to DynamoDB first; localStorage is only a compatibility mirror.
    let dbActivity;
    try {
      dbActivity = await db.updateActivity(activityId, dbUpdates);
    } catch (err) {
      if (isActivityAuthError(err)) {
        throw new Error('Activity update was denied by AppSync. This activity may be missing the shared editor list because it was created by another user before the latest backend fix; repair activityEditors or ask the original creator to save it once.');
      }
      throw err;
    }
    if (!dbActivity && idx === -1) throw new Error('Activity not found');
    const normalizedDbActivity = normalizeActivity(dbActivity);

    if (idx !== -1) {
      Object.assign(activities[idx], dbUpdates, normalizedDbActivity || {});
      save(ACTIVITIES_KEY, activities);
      return activities[idx];
    }

    return normalizedDbActivity || { id: activityId, ...dbUpdates };
  },

  async deleteActivity(activityId) {
    await db.deleteActivity(activityId);
    save(ACTIVITIES_KEY, getStored(ACTIVITIES_KEY).filter((a) => a.id !== activityId));
    return true;
  },

  async concludeActivity(activityId) {
    const activity = await this.getActivity(activityId);
    if (activity?.type === 'betting') {
      const state = await competitionService.load(activity.masterEventId, activityId);
      for (const event of state.betting.events.filter(e => e.status === 'active')) {
        await competitionService.command(activity.masterEventId, activityId, 'concludeEvent', { eventId: event.id });
      }
    }
    return this.updateActivity(activityId, { status: 'concluded', concludedAt: new Date().toISOString() });
  },

  // ===== Betting activity user status =====
  getBettingUserStatus(masterEventId) {
    const members = getStored(MEMBERS_KEY).filter((m) => m.masterEventId === masterEventId);
    const result = {};
    members.forEach((m) => { result[m.userId] = m.status; });
    return result;
  },
};
